import type { BaseSelectionResponse } from "@frend-digital/centra-types/selection";
import {
  useIsFetching,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";
import { useCallback } from "react";
import { useSetLocalToken } from "../auth/store";
import { useGiftToken, useSetGiftToken, useToken } from "../context";
import { useGiftSelectionKey, useSelectionKey } from "../useSelectionKey";
import { type AddToCartOptions } from "./fetchers";
import type { ChangePasswordVariables } from "./fetchers/changePassword";
import type { AddGiftCertOptions } from "./fetchers/giftcard/addGiftCertificate";
import { type LoginVariables } from "./fetchers/loginUser";
import type { ResetPasswordVariables } from "./fetchers/resetPassword";
import type { ResetPasswordEmailVariables } from "./fetchers/resetPasswordEmail";
import { type QuantityVariables } from "./fetchers/setCartItemQuantity";
import type { PromoVariables } from "./fetchers/setPromo";
import type { SignupVariables } from "./fetchers/signUpUser";
import type { NewsletterVariables } from "./fetchers/subscribeNewsletter";
import type { CountryVariables } from "./fetchers/updateCountry";
import type { User } from "./fetchers/updateUser";
import { optimisticUpdateQuantity } from "./optimisticUpdate";
import { type SelectionFetchers } from "./selectionFetchers";

export type DefaultFormattedSelection = {
  rawSelection: BaseSelectionResponse;
  centraCheckoutScript: string | undefined;
};

type MutationOptions<TData, TVars = unknown> = Partial<
  UseMutationOptions<TData, Error, TVars>
>;

type SelectionOptions<T, U> = {
  select?: (data: U) => T;
};

export const buildSelectionHooks = <
  FormattedSelection extends DefaultFormattedSelection,
>({
  fetchers,
}: {
  fetchers: SelectionFetchers<FormattedSelection>;
}) => {
  const useSelection = <T = FormattedSelection>({
    select,
  }: SelectionOptions<T, FormattedSelection> = {}) => {
    const token = useToken();
    const setToken = useSetLocalToken();
    const key = useSelectionKey();

    const response = useQuery({
      queryKey: key,
      queryFn: ({ signal }) =>
        fetchers.getSelection({ token, signal, setToken }),
      select,
    });

    return response;
  };

  const useAddToCart = (
    options: MutationOptions<
      FormattedSelection,
      AddToCartOptions<FormattedSelection>["variables"]
    > = {}
  ) => {
    const token = useToken();
    const selectionKey = useSelectionKey();
    const queryClient = useQueryClient();

    return useMutation({
      ...options,
      mutationKey: ["addToCart", token],
      mutationFn: ({
        itemId,
        comment,
      }: {
        itemId: string;
        comment?: string;
      }) => {
        if (!token) {
          throw new Error("No token found");
        }
        return fetchers.addToCart({ itemId, comment }, token);
      },

      onMutate: async (...args) => {
        queryClient.cancelQueries({ queryKey: selectionKey });
        return options.onMutate?.(...args);
      },

      onSettled: (...args) => {
        const [data] = args;
        if (data) queryClient.setQueryData(selectionKey, data);

        queryClient.invalidateQueries({ queryKey: selectionKey });
        options.onSettled?.(...args);
      },
    });
  };

  const useSetSelection = () => {
    const selectionKey = useSelectionKey();
    const queryClient = useQueryClient();

    return useCallback(
      (
        selection: BaseSelectionResponse,
        { invalidate = false }: { invalidate?: boolean } = {}
      ) => {
        queryClient.setQueryData(selectionKey, fetchers.formatter(selection));
        if (invalidate)
          queryClient.invalidateQueries({ queryKey: selectionKey });
      },
      []
    );
  };

  const useSetQuantity = ({
    optimistic = false,
    onSuccess,
    onError,
    onMutate,
    onSettled,
  }: MutationOptions<FormattedSelection, QuantityVariables> & {
    optimistic?: boolean;
  } = {}) => {
    const token = useToken();
    const selectionKey = useSelectionKey();
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: ["setQuantity", token],
      mutationFn: ({
        line,
        newQuantity,
      }: {
        line: string;
        newQuantity: number;
      }) => {
        if (!token) {
          throw new Error("No token found");
        }
        return fetchers.setCartItemQuantity({ line, newQuantity }, token);
      },

      onMutate: async ({ line, newQuantity }) => {
        if (!optimistic) return onMutate?.({ line, newQuantity });
        queryClient.cancelQueries({ queryKey: selectionKey });
        queryClient.setQueryData(
          selectionKey,
          (oldData: FormattedSelection | undefined) => {
            const rawSelection = oldData?.rawSelection;
            if (!rawSelection) return oldData;

            const updatedSelection = optimisticUpdateQuantity(rawSelection, {
              line,
              newQuantity,
            });

            const formattedSelection = fetchers.formatter(updatedSelection);
            return formattedSelection;
          }
        );

        return onMutate?.({ line, newQuantity });
      },
      onError,
      onSuccess,
      onSettled: (...args) => {
        queryClient.invalidateQueries({ queryKey: selectionKey });
        onSettled?.(...args);
      },
    });
  };

  const useRemoveFromCart = (
    options: MutationOptions<
      FormattedSelection,
      { line: string; optimistic?: boolean }
    > = {},
    optimistic = false
  ) => {
    const setQuantity = useSetQuantity({ optimistic });
    const token = useToken();

    return useMutation({
      mutationKey: ["removeFromCart", token],
      mutationFn: ({ line }: { line: string }) => {
        if (!token) {
          throw new Error("No token found");
        }
        return setQuantity.mutateAsync({ line, newQuantity: 0 });
      },
      ...options,
    });
  };

  const useLogin = (
    options?: MutationOptions<FormattedSelection, LoginVariables>
  ) => {
    const queryClient = useQueryClient();
    const token = useToken();
    const selectionKey = useSelectionKey();

    return useMutation({
      mutationKey: ["login", token],
      mutationFn: async ({ email, password }: LoginVariables) => {
        if (!token) throw new Error("No token found");
        const response = await fetchers.loginUser(
          {
            email,
            password,
          },
          token
        );

        return response;
      },
      ...options,
      // Todo - Optimistic?
      onMutate: async (arg0) => {
        queryClient.cancelQueries({ queryKey: selectionKey });
        return options?.onMutate?.(arg0);
      },
      onSuccess: (...args) => {
        const [data] = args;
        queryClient.setQueryData<FormattedSelection>(selectionKey, data);
      },
      onSettled: (...args) => {
        queryClient.invalidateQueries({ queryKey: selectionKey });

        return options?.onSettled?.(...args);
      },
    });
  };

  const useLogout = ({ onMutate, onError, onSuccess, onSettled }: any = {}) => {
    const queryClient = useQueryClient();
    const token = useToken();
    const selectionKey = useSelectionKey();

    return useMutation({
      mutationKey: ["logout", token],
      mutationFn: async () => {
        if (!token) throw new Error("No token found");
        const response = await fetchers.logoutUser(token);

        return response;
      },
      onSuccess,
      onMutate: async () => {
        queryClient.cancelQueries({ queryKey: selectionKey });
        const oldData =
          queryClient.getQueryData<FormattedSelection>(selectionKey);

        // Remove selection.rawSelection.loggedIn
        if (oldData) {
          queryClient.setQueryData(
            selectionKey,
            (oldData: FormattedSelection | undefined) => {
              const rawSelection = oldData?.rawSelection;
              if (!rawSelection) return oldData;

              const updatedSelection = {
                ...rawSelection,
                loggedIn: undefined,
              };

              const formattedSelection = fetchers.formatter(updatedSelection);
              return formattedSelection;
            }
          );
        }

        await onMutate?.();

        return {
          oldData: oldData!,
        };
      },
      onError: (error, variables, context) => {
        context?.oldData &&
          queryClient.setQueryData(selectionKey, context?.oldData);
        return onError?.(error, variables, context);
      },
      onSettled: (...args) => {
        queryClient.invalidateQueries({ queryKey: selectionKey });
        return onSettled?.(...args);
      },
    });
  };

  const useUpdateUser = (
    options?: MutationOptions<FormattedSelection, User>
  ) => {
    const queryClient = useQueryClient();
    const token = useToken();
    const selectionKey = useSelectionKey();

    return useMutation({
      mutationKey: ["updateUser", token],
      mutationFn: async (variables: User) => {
        if (!token) throw new Error("No token found");
        const response = await fetchers.updateUser(variables, token);

        return response;
      },
      ...options,
      onSuccess: (...args) => {
        options?.onSuccess?.(...args);
        const [data] = args;
        queryClient.setQueryData<FormattedSelection>(selectionKey, data);
      },
      onSettled: (...args) => {
        options?.onSettled?.(...args);
        queryClient.invalidateQueries({ queryKey: selectionKey });
      },
    });
  };

  const useSubscribeNewsletter = (
    options?: MutationOptions<FormattedSelection, NewsletterVariables>
  ) => {
    const queryClient = useQueryClient();
    const token = useToken();
    const selectionKey = useSelectionKey();

    return useMutation({
      mutationKey: ["newsletter", token],
      mutationFn: async (variables: NewsletterVariables) => {
        if (!token) throw new Error("No token found");
        const response = await fetchers.subscribeNewsletter(variables, token);

        return response;
      },
      ...options,
      onSuccess: (...args) => {
        options?.onSuccess?.(...args);
        const [data] = args;
        queryClient.setQueryData<FormattedSelection>(selectionKey, data);
      },
      onSettled: (...args) => {
        options?.onSettled?.(...args);
        queryClient.invalidateQueries({ queryKey: selectionKey });
      },
    });
  };

  const useSignUp = (
    options?: MutationOptions<FormattedSelection, SignupVariables>
  ) => {
    const queryClient = useQueryClient();
    const token = useToken();
    const selectionKey = useSelectionKey();

    return useMutation({
      mutationKey: ["signup", token],
      mutationFn: async (variables: SignupVariables) => {
        if (!token) throw new Error("No token found");
        const response = await fetchers.signUpUser(variables, token);

        return response;
      },
      ...options,
      onSuccess: (...args) => {
        options?.onSuccess?.(...args);
        const [data] = args;
        queryClient.setQueryData<FormattedSelection>(selectionKey, data);
      },
      onSettled: (...args) => {
        options?.onSettled?.(...args);
        queryClient.invalidateQueries({ queryKey: selectionKey });
      },
    });
  };

  const useChangePassword = (
    options?: MutationOptions<FormattedSelection, ChangePasswordVariables>
  ) => {
    const queryClient = useQueryClient();
    const token = useToken();
    const selectionKey = useSelectionKey();

    return useMutation({
      mutationKey: ["changePassword", token],
      mutationFn: async (variables: ChangePasswordVariables) => {
        if (!token) throw new Error("No token found");
        const response = await fetchers.changePassword(variables, token);

        return response;
      },
      ...options,
      onSuccess: (...args) => {
        options?.onSuccess?.(...args);
        const [data] = args;
        queryClient.setQueryData<FormattedSelection>(selectionKey, data);
      },
      onSettled: (...args) => {
        options?.onSettled?.(...args);
        queryClient.invalidateQueries({ queryKey: selectionKey });
      },
    });
  };

  const useAddVoucher = (
    options?: MutationOptions<FormattedSelection, Pick<PromoVariables, "code">>
  ) => {
    const queryClient = useQueryClient();
    const token = useToken();
    const selectionKey = useSelectionKey();

    return useMutation({
      mutationKey: ["addVoucher", token],
      mutationFn: async (variables: Pick<PromoVariables, "code">) => {
        if (!token) throw new Error("No token found");
        const response = await fetchers.vouchers(
          { ...variables, remove: false },
          token
        );
        return response;
      },
      ...options,
      onSuccess: (...args) => {
        options?.onSuccess?.(...args);
        const [data] = args;
        queryClient.setQueryData<FormattedSelection>(selectionKey, data);
      },
      onSettled: (...args) => {
        options?.onSettled?.(...args);
        queryClient.invalidateQueries({ queryKey: selectionKey });
      },
    });
  };

  const useRemoveVoucher = (
    options?: MutationOptions<FormattedSelection, Pick<PromoVariables, "code">>
  ) => {
    const queryClient = useQueryClient();
    const token = useToken();
    const selectionKey = useSelectionKey();

    return useMutation({
      mutationKey: ["removeVoucher", token],
      mutationFn: async (variables: Pick<PromoVariables, "code">) => {
        if (!token) throw new Error("No token found");
        const response = await fetchers.vouchers(
          { ...variables, remove: true },
          token
        );
        return response;
      },
      ...options,
      onSuccess: (...args) => {
        options?.onSuccess?.(...args);
        const [data] = args;
        queryClient.setQueryData<FormattedSelection>(selectionKey, data);
      },
      onSettled: (...args) => {
        options?.onSettled?.(...args);
        queryClient.invalidateQueries({ queryKey: selectionKey });
      },
    });
  };

  const useResetPassword = (
    options?: MutationOptions<FormattedSelection, ResetPasswordVariables>
  ) => {
    const queryClient = useQueryClient();
    const token = useToken();
    const selectionKey = useSelectionKey();

    return useMutation({
      mutationKey: ["resetPassword", token],
      mutationFn: async (variables: ResetPasswordVariables) => {
        const response = await fetchers.resetPassword(variables);
        return response;
      },
      ...options,
      onSuccess: (...args) => {
        options?.onSuccess?.(...args);
        const [data] = args;
        queryClient.setQueryData<FormattedSelection>(selectionKey, data);
      },
      onSettled: (...args) => {
        options?.onSettled?.(...args);
        queryClient.invalidateQueries({ queryKey: selectionKey });
      },
    });
  };

  const useResetPasswordEmail = (
    options?: MutationOptions<FormattedSelection, ResetPasswordEmailVariables>
  ) => {
    const queryClient = useQueryClient();
    const token = useToken();
    const selectionKey = useSelectionKey();

    return useMutation({
      mutationKey: ["resetPasswordEmail", token],
      mutationFn: async (variables: ResetPasswordEmailVariables) => {
        const response = await fetchers.resetPasswordEmail(variables);
        return response;
      },
      ...options,
      onSuccess: (...args) => {
        options?.onSuccess?.(...args);
        const [data] = args;
        queryClient.setQueryData<FormattedSelection>(selectionKey, data);
      },
      onSettled: (...args) => {
        options?.onSettled?.(...args);
        queryClient.invalidateQueries({ queryKey: selectionKey });
      },
    });
  };

  const useSetCountry = (
    options?: MutationOptions<FormattedSelection, CountryVariables>
  ) => {
    const queryClient = useQueryClient();
    const token = useToken();
    const selectionKey = useSelectionKey();

    return useMutation({
      mutationKey: ["setCountry", token],
      mutationFn: async (variables: CountryVariables) => {
        if(!token) {
          throw new Error("No token found")
        }
        const response = await fetchers.updateCountry(variables, token);
        return response;
      },
      ...options,
      onSuccess: (...args) => {
        options?.onSuccess?.(...args);
        const [data] = args
        queryClient.setQueryData<FormattedSelection>(selectionKey, data);
      },
      onSettled: (...args) => {
        options?.onSettled?.(...args)
        queryClient.invalidateQueries({queryKey: selectionKey})
      }
    })
  }

  const useSelectionStatus = () => {
    const selectionKey = useSelectionKey();
    const token = useToken();

    const selectionFetching = useIsFetching({
      queryKey: selectionKey,
    });

    const quantityPending = useIsMutating({
      mutationKey: ["setQuantity", token],
    });
    const addToCartPending = useIsMutating({
      mutationKey: ["addToCart", token],
    });

    const isPending =
      !!selectionFetching || !!quantityPending || !!addToCartPending;

    return {
      /**
       * True when any optimistic mutation is pending or when selection is fetching
       */
      isPending,
    };
  };

  const _selectCheckoutScript = (data: FormattedSelection) =>
    data.centraCheckoutScript;
  const useCentraCheckoutScript = () =>
    useSelection({ select: _selectCheckoutScript });

  const _selectUser = (data: FormattedSelection) => data.rawSelection.loggedIn;
  const useUser = () => useSelection({ select: _selectUser });
  const _selectLoggedIn = (data: FormattedSelection) =>
    !!data.rawSelection.loggedIn;
  const useLoggedIn = () => useSelection({ select: _selectLoggedIn });

  return {
    useToken: useToken,
    useSelectionKey,
    useSelection,
    useAddToCart,
    useSetQuantity,
    useSelectionStatus,
    useCentraCheckoutScript,
    useUser,
    useLoggedIn,
    useLogin,
    useLogout,
    useSetSelection,
    useRemoveFromCart,
    useSignUp,
    useSubscribeNewsletter,
    useAddVoucher,
    useRemoveVoucher,
    useUpdateUser,
    useChangePassword,
    useResetPassword,
    useResetPasswordEmail,
    useSetCountry,
  };
};

export const buildGiftHooks = <
  FormattedSelection extends DefaultFormattedSelection,
>({
  fetchers,
}: {
  fetchers: SelectionFetchers<FormattedSelection>;
}) => {
  const useSetGiftQuantity = ({
    optimistic = false,
    onSuccess,
    onError,
    onMutate,
    onSettled,
  }: MutationOptions<FormattedSelection, QuantityVariables> & {
    optimistic?: boolean;
  } = {}) => {
    const token = useGiftToken();
    const selectionKey = useGiftSelectionKey();
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: ["setGiftQuantity", token],
      mutationFn: ({
        line,
        newQuantity,
      }: {
        line: string;
        newQuantity: number;
      }) => {
        if (!token) {
          throw new Error("No token found");
        }
        return fetchers.setCartItemQuantity({ line, newQuantity }, token);
      },

      onMutate: async ({ line, newQuantity }) => {
        if (!optimistic) return onMutate?.({ line, newQuantity });
        queryClient.cancelQueries({ queryKey: selectionKey });
        queryClient.setQueryData(
          selectionKey,
          (oldData: FormattedSelection | undefined) => {
            const rawSelection = oldData?.rawSelection;
            if (!rawSelection) return oldData;

            const updatedSelection = optimisticUpdateQuantity(rawSelection, {
              line,
              newQuantity,
            });

            const formattedSelection = fetchers.formatter(updatedSelection);
            return formattedSelection;
          }
        );

        return onMutate?.({ line, newQuantity });
      },
      onError,
      onSuccess,
      onSettled: (...args) => {
        queryClient.invalidateQueries({ queryKey: selectionKey });
        onSettled?.(...args);
      },
    });
  };

  const useRemoveGiftCertificate = (
    options: MutationOptions<
      FormattedSelection,
      { line: string; optimistic?: boolean }
    > = {},
    optimistic = false
  ) => {
    const setQuantity = useSetGiftQuantity({ optimistic });
    const token = useGiftToken();

    return useMutation({
      mutationKey: ["removeGiftCertificate", token],
      mutationFn: ({ line }: { line: string }) => {
        if (!token) {
          throw new Error("No token found");
        }
        return setQuantity.mutateAsync({ line, newQuantity: 0 });
      },
    });
  };

  const useGiftSelection = <T = FormattedSelection>({
    select,
  }: SelectionOptions<T, FormattedSelection> = {}) => {
    const token = useGiftToken();
    const setToken = useSetGiftToken();
    const key = useGiftSelectionKey();

    const response = useQuery({
      queryKey: key,
      queryFn: ({ signal }) =>
        fetchers.getSelection({ token, signal, setToken }),
      select,
    });

    return response;
  };

  const useSetGiftSelection = () => {
    const selectionKey = useGiftSelectionKey();
    const queryClient = useQueryClient();

    return useCallback(
      (
        selection: BaseSelectionResponse,
        { invalidate = false }: { invalidate?: boolean } = {}
      ) => {
        queryClient.setQueryData(selectionKey, fetchers.formatter(selection));
        if (invalidate)
          queryClient.invalidateQueries({ queryKey: selectionKey });
      },
      []
    );
  };

  const useInvalidateGiftSelection = () => {
    const selectionKey = useGiftSelectionKey();
    const queryClient = useQueryClient();

    return useCallback(
      () => queryClient.invalidateQueries({queryKey: selectionKey})
    , [])
  }

  const useAddCustomCertificate = (
    options: MutationOptions<
      FormattedSelection,
      AddGiftCertOptions<FormattedSelection>["variables"]
    > = {}
  ) => {
    const token = useGiftToken();
    const selectionKey = useGiftSelectionKey();
    const queryClient = useQueryClient();

    return useMutation({
      ...options,
      mutationKey: ["addCustomGiftCertificate", token],
      mutationFn: ({
        certificate,
        amount,
      }: {
        certificate: string;
        amount: number;
      }) => {
        if (!token) {
          throw new Error("No token found");
        }
        return fetchers.addCustomGiftCertificate(
          { certificate, amount },
          token
        );
      },

      onMutate: async (...args) => {
        queryClient.cancelQueries({ queryKey: selectionKey });
        return options.onMutate?.(...args);
      },

      onSettled: (...args) => {
        const [data] = args;
        if (data) queryClient.setQueryData(selectionKey, data);

        queryClient.invalidateQueries({ queryKey: selectionKey });
        options.onSettled?.(...args);
      },
    });
  };

  const useAddGiftCertificate = (
    options: MutationOptions<
      FormattedSelection,
      AddGiftCertOptions<FormattedSelection>["variables"]
    > = {}
  ) => {
    const token = useGiftToken();
    const selectionKey = useGiftSelectionKey();
    const queryClient = useQueryClient();

    return useMutation({
      ...options,
      mutationKey: ["addGiftCertificate", token],
      mutationFn: ({ certificate }: { certificate: string }) => {
        if (!token) {
          throw new Error("No token found");
        }
        return fetchers.addGiftCertificate({ certificate }, token);
      },

      onMutate: async (...args) => {
        queryClient.cancelQueries({ queryKey: selectionKey });
        return options.onMutate?.(...args);
      },

      onSettled: (...args) => {
        const [data] = args;
        if (data) queryClient.setQueryData(selectionKey, data);

        queryClient.invalidateQueries({ queryKey: selectionKey });
        options.onSettled?.(...args);
      },
    });
  };

  const useSetGiftCountry = (
    options?: MutationOptions<FormattedSelection, CountryVariables>
  ) => {
    const queryClient = useQueryClient();
    const token = useGiftToken();
    const selectionKey = useGiftSelectionKey();

    return useMutation({
      mutationKey: ["setCountry", token],
      mutationFn: async (variables: CountryVariables) => {
        if(!token) {
          throw new Error("No token found")
        }
        const response = await fetchers.updateCountry(variables, token);
        return response;
      },
      ...options,
      onSuccess: (...args) => {
        options?.onSuccess?.(...args);
        const [data] = args
        queryClient.setQueryData<FormattedSelection>(selectionKey, data);
      },
      onSettled: (...args) => {
        options?.onSettled?.(...args)
        queryClient.invalidateQueries({queryKey: selectionKey})
      }
    })
  }

  return {
    useAddGiftCertificate,
    useAddCustomCertificate,
    useGiftSelection,
    useSetGiftSelection,
    useInvalidateGiftSelection,
    useSetGiftQuantity,
    useRemoveGiftCertificate,
    useSetGiftCountry
  };
};
